import React from 'react';
import { ExternalLink, Heart } from 'lucide-react';
import { SUPPLIER_LOGOS } from '../../data/productsCatalog';
import { useFavourites } from '../../context/FavouritesContext';
import CompareToggle from '../compare/CompareToggle';

const API = process.env.REACT_APP_BACKEND_URL;

export const money = (v) => {
  const n = parseFloat(String(v ?? '').replace(/[^0-9.]/g, ''));
  return Number.isFinite(n) ? `$${n.toLocaleString('en-AU', { maximumFractionDigits: 0 })}` : '';
};

// Some supplier feeds (e.g. Lorraine Lea) dump a Shopify collection list into
// the category field, which surfaces as a junk "kind" like "All active
// products in Shopify, …". Hide anything that isn't a clean, short label.
const cleanKind = (k) => {
  const s = String(k || '').trim();
  if (!s) return '';
  const low = s.toLowerCase();
  if (low.includes('all active products') || low.includes('shopify') || s.includes(',') || s.length > 36) return '';
  return s;
};

/**
 * AffiliateProductCard — a single tracked product tile, shared by the
 * /products featured strip and the /products/<department> browse grid.
 * Outbound link routes through the tracked /api/affiliate/go proxy.
 */
const AffiliateProductCard = ({ product: p, showBrand = true }) => {
  const { toggleFavourite, isFavourite, openSidebarOn } = useFavourites();
  const faved = isFavourite(p, 'product');
  return (
  <a
    href={`${API}${p.go_url}`}
    target="_blank"
    rel="sponsored nofollow noopener noreferrer"
    className="group flex flex-col bg-white border border-gray-200 rounded-xl overflow-hidden hover:-translate-y-0.5 hover:shadow-lg hover:border-[#1d8efe]/40 transition-all duration-300"
    data-testid={`product-card-${p.id}`}
  >
    <div className="aspect-square bg-[#f6f7f9] overflow-hidden relative">
      {p.save > 0 && (
        <span className="absolute z-10 top-2 left-2 bg-[#e5322d] text-white font-bold rounded-md shadow-sm text-[10.5px] px-1.5 py-0.5">
          Save ${p.save}
        </span>
      )}
      {/* Save to my build - keeps product in the user's shortlist */}
      <button
        type="button"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleFavourite(p, 'product');
          if (!faved) setTimeout(() => openSidebarOn('product'), 250);
        }}
        aria-label={faved ? 'Remove from my build' : 'Save to my build'}
        title={faved ? 'Saved to my build' : 'Save to my build'}
        data-testid={`product-save-${p.id}`}
        className={`absolute z-10 bottom-2 left-2 inline-flex items-center justify-center w-8 h-8 rounded-full backdrop-blur-sm shadow-sm transition-all ${
          faved ? 'bg-red-500 text-white' : 'bg-white/95 text-[#1d1d1f] hover:bg-white ring-1 ring-black/5'
        }`}
      >
        <Heart className={`w-4 h-4 ${faved ? 'fill-current' : ''}`} />
      </button>
      <div className="absolute z-10 bottom-2 right-2">
        <CompareToggle
          item={{ __label: p.name, __href: `${API}${p.go_url}`, __logo: p.image || null, __sub: p.supplier?.name || '', price: p.price, list_price: p.list_price, save: p.save, supplier: p.supplier, product_kind: cleanKind(p.kind) }}
          kind="product"
          id={p.id}
          label="Compare"
        />
      </div>
      {/* Supplier logo badge — always shown so the source is clear */}
      {p.supplier?.key && (
        <span
          className="absolute z-10 top-2 right-2 bg-white rounded-md shadow-sm ring-1 ring-black/5 h-6 min-w-[40px] max-w-[88px] px-1.5 flex items-center justify-center"
          title={p.supplier?.name}
          data-testid={`product-supplier-badge-${p.supplier.key}`}
        >
          {SUPPLIER_LOGOS[p.supplier.key] ? (
            <img
              src={SUPPLIER_LOGOS[p.supplier.key]}
              alt={p.supplier?.name || p.supplier.key}
              loading="lazy"
              className="max-h-4 max-w-[76px] object-contain"
              onError={(e) => { e.currentTarget.style.display = 'none'; const s = e.currentTarget.nextSibling; if (s) s.style.display = 'inline'; }}
            />
          ) : null}
          <span
            style={{ display: SUPPLIER_LOGOS[p.supplier.key] ? 'none' : 'inline' }}
            className="text-[9.5px] font-bold text-[#1d1d1f] uppercase tracking-wide whitespace-nowrap"
          >
            {p.supplier?.name || p.supplier.key}
          </span>
        </span>
      )}
      {p.image && (
        <img
          src={p.image}
          alt={p.name}
          loading="lazy"
          decoding="async"
          className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300 p-3"
        />
      )}
    </div>
    <div className="flex-1 flex flex-col p-3.5">
      {cleanKind(p.kind) && (
        <p className="font-bold uppercase tracking-[0.05em] text-[#1d8efe] truncate text-[10.5px] mb-1">{cleanKind(p.kind)}</p>
      )}
      <p className="text-[#1d1d1f] line-clamp-2 text-[13px] leading-[1.35] min-h-[35px]">{p.name}</p>
      <div className="flex items-center justify-between mt-2">
        <div className="flex items-baseline gap-1.5">
          {money(p.price)
            ? <span className="font-bold text-[#1d1d1f] text-[14px]">{money(p.price)}</span>
            : <span className="font-semibold text-[#9aa0a8] text-[12px]">Price: TBA</span>}
          {p.save > 0 && money(p.list_price) && (
            <span className="text-[11px] text-[#9aa0a8] line-through">{money(p.list_price)}</span>
          )}
        </div>
        <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#1d8efe] ml-auto">
          View<ExternalLink className="w-3 h-3" />
        </span>
      </div>
      {showBrand && p.supplier?.name && (
        <p className="text-[#9aa0a8] truncate text-[10.5px] mt-1.5">from {p.supplier.name}</p>
      )}
    </div>
  </a>
  );
};

export default AffiliateProductCard;
